import * as React from 'react';
import { ISteppedWeirCrossProps } from './ISteppedWeir';
import { getImgSrcWithColor, loadImage } from '../../../../../utils/graphicEngine';
import groundImage from '../../../../../images/ground_pattern.svg';
import textureProtectionSystem from '../../../../../images/texture_protection_system.svg';
import { setCrossSectionImg } from '../../../../store/project/resultsSlice';
import { useAppDispatch } from '../../../../../../reduxService/hooks';
import { roundToOne } from '../../../../../MACRA_1/components/InputForm/Geometry/utils';
import { EConversionSIToImperial, convertTo } from '../../../../../conversion';

//SETUP
const setup = {
    gabionColor: 'grey',
    borderGabionColor: 'black',
    waterColor: '#83d7ee',

    indicatorHeight: 30,
    indicatorWidth: 40,
    font: '11px Arial',
    colorIndicator: 'red',
    indicatorLineWidth: 1,

    borderWidth: 2,
    height: 370,
    width: 540,
    heightSoil: 2,
};
//

const SteppedWeirCross = (props: ISteppedWeirCrossProps) => {
    const steppedWeirCanvas = React.useRef<HTMLCanvasElement>(null);
    let ctx: CanvasRenderingContext2D;

    let soilImageSrc: HTMLImageElement;
    let baseImageSrc: HTMLImageElement | undefined = undefined;

    const { weirGeometry, wingsGeometry, riverGeometry, result, scale } = props;
    const dispatch = useAppDispatch();

    const _height = riverGeometry!.bankTopElevation + weirGeometry!.foundationLayers! * weirGeometry.stepHeight + setup.heightSoil;
    const _width = setup.borderWidth + riverGeometry.channelWidth + setup.borderWidth;

    const scaleWidth = (setup.width + scale) / _width;
    const scaleHeight = (setup.height + scale) / _height;

    const _scale = Math.ceil(Math.min(scaleWidth, scaleHeight));

    React.useEffect(() => {
        let steppedWeirCanvasEle = steppedWeirCanvas.current!;
        ctx = steppedWeirCanvasEle.getContext('2d')!;
        Promise.all([loadSoilImage(), loadBaseImage()]).then(() => {
            cleanCanvas();
            if (!!result) {
                createWaterSection();
            }
            createRiverGeometry();
            createIndicator();
            dispatch(setCrossSectionImg(steppedWeirCanvasEle.toDataURL('image/png')));
        });
    }, [props]);

    const createWaterSection = () => {
        let z0 = result!.hydraulicUpstream.backwaterDepth;
        ctx.beginPath();
        ctx.fillStyle = setup.waterColor;
        ctx.moveTo(setup.indicatorWidth, _scale * (riverGeometry!.bankTopElevation - weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - z0));
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * (riverGeometry!.bankTopElevation - weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!) - z0));
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * (riverGeometry!.bankTopElevation - weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!)));
        ctx.lineTo(setup.indicatorWidth, _scale * (riverGeometry!.bankTopElevation - weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!)));
        ctx.fill();
        ctx.closePath();
    };

    const loadSoilImage = async (): Promise<void> => {
        soilImageSrc = await loadImage(groundImage);
    };

    const createIndicator = () => {
        ctx.save();
        //river geometry bank top elevation
        createIndicatorY(0, _scale * riverGeometry.bankTopElevation, setup.indicatorWidth / 2, riverGeometry.bankTopElevation);
        //weir geometry height
        createIndicatorY(
            _scale * (riverGeometry.bankTopElevation - weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!)),
            _scale * riverGeometry.bankTopElevation,
            setup.indicatorWidth + _scale * _width + setup.indicatorWidth / 2,
            weirGeometry.stepHeight * (weirGeometry.stepNumber! - weirGeometry.foundationLayers!),
        );
        //river geometry channel width
        createIndicatorX(
            setup.indicatorWidth + _scale * setup.borderWidth,
            setup.indicatorWidth + _scale * (setup.borderWidth + riverGeometry.channelWidth),
            _scale * _height + setup.indicatorHeight / 2,
            riverGeometry.channelWidth,
        );
        ctx.restore();
    };

    const createIndicatorY = (moveToY: number, lineToY: number, x: number, value: number) => {
        createLine([], x - 2, moveToY, x + 2, moveToY);
        createLine([], x, moveToY, x, lineToY, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], x - 2, lineToY, x + 2, lineToY);
    };

    const createIndicatorX = (moveToX: number, lineToX: number, y: number, value: number) => {
        createLine([], moveToX, y - 2, moveToX, y + 2);
        createLine([], moveToX, y, lineToX, y, roundToOne(props.isImperial ? convertTo(value, EConversionSIToImperial.m_to_ft) : value));
        createLine([], lineToX, y - 2, lineToX, y + 2);
    };

    const createLine = (segments: number[], moveToX: number, moveToY: number, lineToX: number, lineToY: number, value?: number) => {
        ctx.beginPath();
        ctx.setLineDash(segments);
        ctx.moveTo(moveToX, moveToY);
        ctx.lineTo(lineToX, lineToY);
        ctx.strokeStyle = setup.colorIndicator!;
        ctx.font = setup.font;
        ctx.lineWidth = setup.indicatorLineWidth;
        ctx.lineCap = 'round';
        if (value && value > 0) {
            ctx.fillStyle = 'black';
            ctx.fillText(value.toString(), (moveToX + lineToX) / 2 + 2, (moveToY + lineToY) / 2 - 2);
        }
        ctx.stroke();
    };

    const loadBaseImage = async (): Promise<void> => {
        let imgSrcWithColor = getImgSrcWithColor(textureProtectionSystem, setup.gabionColor);
        baseImageSrc = await loadImage(imgSrcWithColor);
    };

    const cleanCanvas = () => {
        let canvasEle = steppedWeirCanvas.current!;
        ctx = canvasEle.getContext('2d')!;
        ctx.clearRect(0, 0, canvasEle.width, canvasEle.height);
    };

    const createRiverGeometry = () => {
        //river geometry
        let pat = ctx.createPattern(soilImageSrc, 'repeat')!;
        ctx.beginPath();
        ctx.fillStyle = pat;
        ctx.moveTo(setup.indicatorWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * setup.borderWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * setup.borderWidth, _scale * riverGeometry!.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + riverGeometry!.channelWidth), _scale * riverGeometry!.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + riverGeometry!.channelWidth), 0);
        ctx.lineTo(setup.indicatorWidth + _scale * _width, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * _width, _scale * _height);
        ctx.lineTo(setup.indicatorWidth, _scale * _height);
        ctx.lineTo(setup.indicatorWidth, 0);
        ctx.fill();
        ctx.closePath();
        ctx.beginPath();
        ctx.moveTo(setup.indicatorWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * setup.borderWidth, 0);
        ctx.lineTo(setup.indicatorWidth + _scale * setup.borderWidth, _scale * riverGeometry!.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + riverGeometry!.channelWidth), _scale * riverGeometry!.bankTopElevation);
        ctx.lineTo(setup.indicatorWidth + _scale * (setup.borderWidth + riverGeometry!.channelWidth), 0);
        ctx.lineTo(setup.indicatorWidth + _scale * _width, 0);
        ctx.stroke();
        ctx.closePath();

        ctx.beginPath();
        let basePat = ctx.createPattern(baseImageSrc!, 'repeat');

        for (let i = 0; i <= weirGeometry.stepNumber!; i++) {
            if (i < weirGeometry.foundationLayers!) {
                //foundationLayer
                createRectWithBorder(setup.indicatorWidth, _scale * (riverGeometry!.bankTopElevation + weirGeometry.stepHeight * i), _scale * _width, _scale * weirGeometry.stepHeight, basePat!);
            } else if (i == weirGeometry.stepNumber!) {
                //wings
                createRectWithBorder(
                    setup.indicatorWidth,
                    _scale * (riverGeometry!.bankTopElevation - weirGeometry.stepHeight * (i - weirGeometry.foundationLayers!) - wingsGeometry.height!),
                    _scale * (_width / 2 - wingsGeometry.width / 2 + wingsGeometry.offset),
                    _scale * wingsGeometry.height!,
                    basePat!,
                );
                createRectWithBorder(
                    setup.indicatorWidth + _scale * (_width / 2 + wingsGeometry.width / 2 + wingsGeometry.offset),
                    _scale * (riverGeometry!.bankTopElevation - weirGeometry.stepHeight * (i - weirGeometry.foundationLayers!) - wingsGeometry.height!),
                    _scale * (_width / 2 - wingsGeometry.width / 2 - wingsGeometry.offset),
                    _scale * wingsGeometry.height!,
                    basePat!,
                );
            } else {
                createRectWithBorder(
                    setup.indicatorWidth,
                    _scale * (riverGeometry!.bankTopElevation - weirGeometry.stepHeight * (i + 1 - weirGeometry.foundationLayers!)),
                    _scale * _width,
                    _scale * weirGeometry.stepHeight,
                    basePat!,
                );
            }
        }
        ctx.closePath();
    };

    const createRectWithBorder = (x: number, y: number, w: number, h: number, pat: string | CanvasPattern | CanvasGradient) => {
        ctx.fillStyle = pat;
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = setup.borderGabionColor;
        ctx.strokeRect(x, y, w, h);
    };

    return <canvas width={_scale * _width + 2 * setup.indicatorWidth} height={_scale * _height + setup.indicatorHeight} ref={steppedWeirCanvas}></canvas>;
};

export default SteppedWeirCross;
